import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon, BookmarkIcon, CheckIcon, CopyIcon, EyeIcon, EyeOffIcon, GripVerticalIcon, LayersIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { useAuth, useWorkspace } from '../../contexts/AuthContext';
import { newBlock, saveLanding, setLandingStatus } from '../../services/landingService';
import { newId } from '../../services/authService';
import { deleteComponent, instantiateComponent, saveComponent } from '../../services/componentService';
import { BLOCK_LIBRARY, blockDefinition } from '../../data/blockLibrary';
import { FONT_OPTIONS } from '../../data/fonts';
import { LandingBlock, LandingPage, LandingTheme } from '../../types';
import { BlockSettings } from '../../components/landings/BlockSettings';
import { StyleSettings } from '../../components/landings/StyleSettings';
import { LogoUploader } from '../../components/shared/LogoUploader';
import { DevicePreview, Device } from '../../components/shared/DevicePreview';
import { LandingRenderer } from '../../components/public/LandingRenderer';
import { ThankYouView } from '../../components/public/ThankYouView';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Textarea } from '../../components/ui/Textarea';
import { Switch } from '../../components/ui/Switch';
import { Modal } from '../../components/ui/Modal';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/Tabs';
import { toast } from '../../components/ui/Toast';
import { copyToClipboard, landingUrl } from '../../utils/clipboard';
import { normalizeSlug, validateSlug } from '../../config/app';
const THEMES: {
  value: LandingTheme;
  label: string;
}[] = [{
  value: 'blanco_premium',
  label: 'Blanco premium'
}, {
  value: 'claro_corporativo',
  label: 'Claro corporativo'
}, {
  value: 'minimalista',
  label: 'Minimalista'
}];
export function LandingEditor() {
  const {
    id = ''
  } = useParams();
  const {
    organization
  } = useAuth();
  const {
    landings,
    forms,
    components
  } = useWorkspace();
  const stored = landings.find(l => l.id === id);
  const [draft, setDraft] = useState<LandingPage | null>(stored ?? null);
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  /** Solo se arrastra desde el asa, para no mover bloques por accidente. */
  const [handleId, setHandleId] = useState<string | null>(null);
  const [device, setDevice] = useState<Device>('desktop');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  /** Pestaña activa: con «gracias» la vista previa muestra la página de gracias. */
  const [tab, setTab] = useState('bloques');
  const [saveComponentFor, setSaveComponentFor] = useState<LandingBlock | null>(null);
  const [componentName, setComponentName] = useState('');
  useEffect(() => {
    if (stored && (!draft || draft.id !== stored.id)) setDraft(stored);
  }, [stored, draft]);
  if (!draft) {
    return <div className="rounded-2xl border border-border bg-background p-10 text-center" data-id="element-1131" data-mp="370kzh66sh95y">
        <p className="text-sm text-muted-foreground" data-id="element-1132" data-mp="3bxuxha88ut5e">Esta landing no existe.</p>
        <Link to="/app/landings" className="mt-4 inline-block" data-id="element-1133" data-mp="3gwfs8u4q8kmg">
          <Button variant="secondary" data-id="element-1134" data-mp="3lofx90znm3pw">Volver a landings</Button>
        </Link>
      </div>;
  }
  const form = forms.find(f => f.id === draft.formId);
  // Disponibilidad del enlace público: se comprueba contra el resto de landings.
  const slugCheck = validateSlug(draft.slug, landings.filter(l => l.id !== draft.id).map(l => l.slug));
  const blocks = draft.pageConfig.blocks;
  const selectedBlock = blocks.find(b => b.id === selectedBlockId) ?? null;
  function update(partial: Partial<LandingPage>) {
    setDraft(prev => prev ? {
      ...prev,
      ...partial
    } : prev);
    setSaved(false);
  }
  function updateConfig(partial: Partial<LandingPage['pageConfig']>) {
    update({
      pageConfig: {
        ...draft!.pageConfig,
        ...partial
      }
    });
  }
  function updateBlock(block: LandingBlock) {
    updateConfig({
      blocks: blocks.map(b => b.id === block.id ? block : b)
    });
  }
  function addBlock(block: LandingBlock) {
    updateConfig({
      blocks: [...blocks, block]
    });
    setSelectedBlockId(block.id);
    setAddOpen(false);
  }
  function duplicateBlock(block: LandingBlock) {
    const copy: LandingBlock = {
      ...(JSON.parse(JSON.stringify(block)) as LandingBlock),
      // El id debe ser un UUID válido: `landing_blocks.id` es de tipo uuid.
      id: newId('block')
    };
    const index = blocks.findIndex(b => b.id === block.id);
    const next = [...blocks];
    next.splice(index + 1, 0, copy);
    updateConfig({
      blocks: next
    });
  }
  /**
   * Reordena solo en vertical: se mueve el objeto completo del bloque, así que
   * conserva id, type, config, style, contenido y visibilidad.
   */
  function reorder(targetId: string) {
    setDragOverId(null);
    setHandleId(null);
    if (!dragId || dragId === targetId) {
      setDragId(null);
      return;
    }
    const next = [...blocks];
    const fromIndex = next.findIndex(b => b.id === dragId);
    const toIndex = next.findIndex(b => b.id === targetId);
    next.splice(toIndex, 0, next.splice(fromIndex, 1)[0]);
    updateConfig({
      blocks: next
    });
    setDragId(null);
  }
  async function handleSave(status?: LandingPage['status']) {
    // El enlace público debe ser válido y único antes de guardar o publicar.
    const check = validateSlug(draft!.slug, landings.filter(l => l.id !== draft!.id).map(l => l.slug));
    if (!check.valid) {
      setSaveError(check.reason ?? 'El enlace no es válido.');
      toast.error('Revisa el enlace público', {
        description: check.reason
      });
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      await saveLanding(draft!);
      if (status) {
        await setLandingStatus(draft!.id, status);
        update({
          status
        });
      }
      setSaved(true);
      // Guardar nunca cambia de página ni recarga: solo confirma.
      if (status === 'publicada') {
        toast.success('Landing publicada correctamente', {
          description: landingUrl(draft!.slug)
        });
      } else {
        toast.success('Guardado correctamente');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error desconocido.';
      setSaveError(message);
      toast.error('No se ha podido guardar', {
        description: message
      });
    } finally {
      setSaving(false);
    }
  }
  /** Copia la URL pública, con confirmación visible. */
  async function handleCopyLink() {
    const ok = await copyToClipboard(landingUrl(draft!.slug));
    if (ok) toast.success('Enlace copiado');else toast.error('No se ha podido copiar el enlace', {
      description: landingUrl(draft!.slug)
    });
  }
  async function confirmSaveComponent() {
    if (!saveComponentFor || !organization) return;
    await saveComponent(organization.id, componentName || blockDefinition(saveComponentFor.type).label, blockDefinition(saveComponentFor.type).label, saveComponentFor);
    setSaveComponentFor(null);
    setComponentName('');
  }
  return <>
      <Link to="/app/landings" className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-id="element-1135" data-mp="lme61d908ytr">
        <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-1136" data-mp="qdmjqlhdb6cq" />
        Volver a landings
      </Link>

      <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between" data-id="element-1137" data-mp="3p21uiat5mls7">
        <div data-id="element-1138" data-mp="3u0mtk6wo6zf2">
          <h1 className="text-xl font-semibold tracking-tight text-navy sm:text-2xl" data-id="element-1139" data-mp="3u03c0raxmd1k">
            {draft.name}
          </h1>
          <p className="break-all text-sm text-muted-foreground" data-id="element-1140" data-mp="1c4tit92y42rv">
            {landingUrl(draft.slug)} ·{' '}
            {draft.status === 'publicada' ? 'Publicada' : draft.status === 'borrador' ? 'Borrador' : 'Despublicada'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2" data-id="element-1141" data-mp="1h1bms6qgbx35">
          {saving ? <span className="self-center text-sm text-muted-foreground" data-id="element-1142" data-mp="1v1mefbzl5fpd">
              Guardando…
            </span> : saved ? <span className="flex items-center gap-1.5 self-center text-sm text-green-600" data-id="element-1143" data-mp="1r0sraktyw4xj">
              <CheckIcon className="h-4 w-4" data-id="element-1144" data-mp="24s794oc52030" /> Guardado correctamente
            </span> : <span className="self-center text-sm text-muted-foreground" data-id="element-1145" data-mp="1zskwag082luq">
              Cambios sin guardar
            </span>}
          <Button variant="ghost" onClick={() => handleSave('borrador')} loading={saving} data-id="element-1146" data-mp="2epbz6z93ufp3">
            Guardar borrador
          </Button>
          <Button variant="secondary" leftIcon={<EyeIcon className="h-4 w-4" data-id="element-1148" data-mp="2tckdi8xpn15a" />} onClick={() => window.open(`/${draft.slug}`, '_blank')} data-id="element-1147" data-mp="29sbsqn8cfaxg">
            Vista previa
          </Button>
          <Button variant="secondary" leftIcon={<CopyIcon className="h-4 w-4" data-id="element-1150" data-mp="2xc1iz2d4zyag" />} onClick={handleCopyLink} data-id="element-1149" data-mp="2xd47x4qwfqnq">
            Copiar enlace
          </Button>
          <Button onClick={() => handleSave('publicada')} loading={saving} data-id="element-1151" data-mp="2odh8upln6wxu">
            {draft.status === 'publicada' ? 'Actualizar publicación' : 'Publicar'}
          </Button>
        </div>
      </div>

      {saveError && <div role="alert" className="mb-5 flex flex-col gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 sm:flex-row sm:items-center sm:justify-between" data-id="element-1152" data-mp="2eftcjv8e1hdk">
          <span data-id="element-1153" data-mp="25mzll0jkwtjv">
            <strong className="font-semibold" data-id="element-1154" data-mp="25mgc887zvdhg">Error al guardar. </strong>
            {saveError}
          </span>
          <Button variant="secondary" onClick={() => handleSave()} loading={saving} data-id="element-1155" data-mp="1qqqlxxvs0e6x">
            Reintentar
          </Button>
        </div>}

      <div className="grid items-start gap-5 xl:grid-cols-[380px_minmax(0,1fr)]" data-id="element-1156" data-mp="1vqwh3o198itr">
        {/* Panel izquierdo, con scroll propio para no perder la vista previa */}
        <div className="min-w-0 xl:max-h-[860px] xl:overflow-y-auto xl:pr-1" data-id="element-1157" data-mp="z84b3z64r9pp">
          <Tabs defaultValue="bloques" value={tab} onValueChange={setTab} data-id="element-1158" data-mp="kabx5bg8etdm">
            <TabsList data-id="element-1159" data-mp="p8wwl8nnt910">
              <TabsTrigger value="bloques" data-id="element-1160" data-mp="p83krt9uftc0">Bloques</TabsTrigger>
              <TabsTrigger value="diseno" data-id="element-1161" data-mp="paq2umud1r8h">Diseño</TabsTrigger>
              <TabsTrigger value="gracias" data-id="element-1162" data-mp="1czsm1z4n7s53">Gracias</TabsTrigger>
            </TabsList>

            <TabsContent value="bloques" data-id="element-1163" data-mp="1hwiymch8o5lr">
              <p className="mb-3 text-xs text-muted-foreground" data-id="element-1164" data-mp="1hzew5zp6cs9o">
                Haz clic en cualquier sección de la vista previa para editarla, o
                arrastra los bloques para reordenarlos.
              </p>
              <ul className="space-y-2" data-id="element-1165" data-mp="1vxlnewzmbu0d">
                {blocks.map(block => {
                const definition = blockDefinition(block.type);
                const active = selectedBlockId === block.id;
                return <li key={block.id} draggable={handleId === block.id} onDragStart={e => {
                  setDragId(block.id);
                  e.dataTransfer.effectAllowed = 'move';
                }} onDragEnd={() => {
                  setDragId(null);
                  setDragOverId(null);
                  setHandleId(null);
                }} onDragOver={e => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  if (dragId && dragId !== block.id) setDragOverId(block.id);
                }} onDragLeave={() => setDragOverId(prev => prev === block.id ? null : prev)} onDrop={() => reorder(block.id)} className={`relative rounded-xl border bg-background shadow-sm transition-colors ${active ? 'border-primary' : 'border-border'} ${dragId === block.id ? 'opacity-50' : ''} ${dragOverId === block.id ? 'before:absolute before:-top-1 before:left-0 before:h-0.5 before:w-full before:rounded-full before:bg-primary' : ''}`} data-id="element-1166" data-mp="3ldxkyx7lgv77">
                      <div className="flex items-center gap-1 p-2.5" data-id="element-1167" data-mp="3fjgvws384c5y">
                        <span onPointerDown={() => setHandleId(block.id)} onPointerUp={() => setHandleId(null)} className="flex h-8 w-6 flex-none cursor-grab items-center justify-center text-muted-foreground active:cursor-grabbing" aria-hidden="true" data-id="element-1168" data-mp="3flbhm8mbk8b3">
                          <GripVerticalIcon className="h-4 w-4" data-id="element-1169" data-mp="3thp11naeyjs7" />
                        </span>
                        <button type="button" onClick={() => setSelectedBlockId(active ? null : block.id)} className="min-w-0 flex-1 text-left" data-id="element-1170" data-mp="2wtiq6imwev5h">
                          <span className="block truncate text-sm font-medium text-foreground" data-id="element-1171" data-mp="31p6e6upn9i9e">
                            {definition.label}
                          </span>
                          <span className="block truncate text-xs text-muted-foreground" data-id="element-1172" data-mp="3alfcg61vyaty">
                            {definition.description}
                          </span>
                        </button>
                        <button type="button" aria-label="Guardar en la biblioteca" onClick={() => {
                      setSaveComponentFor(block);
                      setComponentName(definition.label);
                    }} className="rounded-md p-2 text-muted-foreground hover:bg-surface" data-id="element-1173" data-mp="2i1qnp787dbl6">
                          <BookmarkIcon className="h-4 w-4" data-id="element-1174" data-mp="21hj7yp1sctad" />
                        </button>
                        <button type="button" aria-label={block.visible ? 'Ocultar bloque' : 'Mostrar bloque'} onClick={() => updateBlock({
                      ...block,
                      visible: !block.visible
                    })} className="rounded-md p-2 text-muted-foreground hover:bg-surface" data-id="element-1175" data-mp="1wjzsxmx9kchd">
                          {block.visible ? <EyeIcon className="h-4 w-4" data-id="element-1176" data-mp="1dtrtpfwoq66g" /> : <EyeOffIcon className="h-4 w-4" data-id="element-1177" data-mp="1duktfwx87vf3" />}
                        </button>
                        <button type="button" aria-label="Duplicar bloque" onClick={() => duplicateBlock(block)} className="rounded-md p-2 text-muted-foreground hover:bg-surface" data-id="element-1178" data-mp="18v89bjxqp2g7">
                          <CopyIcon className="h-4 w-4" data-id="element-1179" data-mp="33y3hellbmlzf" />
                        </button>
                        {definition.removable && <button type="button" aria-label="Eliminar bloque" onClick={() => updateConfig({
                      blocks: blocks.filter(b => b.id !== block.id)
                    })} className="rounded-md p-2 text-muted-foreground hover:bg-surface hover:text-red-600" data-id="element-1180" data-mp="38xg2b0smtizd">
                            <Trash2Icon className="h-4 w-4" data-id="element-1181" data-mp="2l7lrspvsc6ia" />
                          </button>}
                      </div>

                      {active && <div className="border-t border-border p-4" data-id="element-1182" data-mp="2pa0ddhqu9kd9">
                          <Tabs defaultValue="contenido" data-id="element-1183" data-mp="2b9ftei96tii6">
                            <TabsList data-id="element-1184" data-mp="2bb0oc8zez3gk">
                              <TabsTrigger value="contenido" data-id="element-1185" data-mp="2g7r0il83lewp">Contenido</TabsTrigger>
                              <TabsTrigger value="estilo" data-id="element-1186" data-mp="9ht72u08qbs1">Estilo</TabsTrigger>
                            </TabsList>
                            <TabsContent value="contenido" data-id="element-1187" data-mp="ehfm98zmq23h">
                              <BlockSettings block={block} onChange={updateBlock} data-id="element-1188" data-mp="eetd54x3bej5" />
                            </TabsContent>
                            <TabsContent value="estilo" data-id="element-1189" data-mp="1u03qmnpne3u9">
                              <StyleSettings block={block} onChange={updateBlock} data-id="element-1190" data-mp="1u1eraensqvh5" />
                            </TabsContent>
                          </Tabs>
                        </div>}
                    </li>;
              })}
              </ul>
              <Button className="mt-4 w-full" variant="secondary" leftIcon={<PlusIcon className="h-4 w-4" data-id="element-1192" data-mp="2hnlc61ibk5os" />} onClick={() => setAddOpen(true)} data-id="element-1191" data-mp="2hp64rll2pek0">
                Añadir sección
              </Button>
            </TabsContent>

            <TabsContent value="diseno" data-id="element-1193" data-mp="2rnvqjpg3bj5j">
              <div className="space-y-4 rounded-2xl border border-border bg-background p-5" data-id="element-1194" data-mp="2rlja5gpzdgul">
                <Input id="l-name" label="Nombre interno" value={draft.name} onChange={e => update({
                name: e.target.value
              })} data-id="element-1195" data-mp="2mpam1dtt2dba" />
                <div data-id="element-1196" data-mp="sb9rv697aehj">
                  <Input id="l-slug" label="Slug (enlace público)" helperText={landingUrl(draft.slug)} value={draft.slug} error={slugCheck.valid ? undefined : slugCheck.reason} onChange={e => update({
                  slug: normalizeSlug(e.target.value)
                })} data-id="element-1197" data-mp="sbt6aczn6kqt" />
                  {slugCheck.valid && <p className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-green-600" data-id="element-1198" data-mp="1qyk5xa2who7h">
                      <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1199" data-mp="1vzzq6zx97qot" />
                      Enlace disponible
                    </p>}
                </div>
                <Select id="l-form" label="Formulario asociado" placeholder="Sin formulario" options={forms.map(f => ({
                value: f.id,
                label: f.name
              }))} value={draft.formId ?? ''} onChange={e => update({
                formId: e.target.value || undefined
              })} data-id="element-1200" data-mp="24yl2idc4u9h5" />
                <Select id="l-theme" label="Tema visual" options={THEMES} value={draft.pageConfig.theme} onChange={e => updateConfig({
                theme: e.target.value as LandingTheme
              })} data-id="element-1201" data-mp="2jp23jvkuuxdi" />
                <Input id="l-color" label="Color principal" type="color" value={draft.pageConfig.primaryColor} onChange={e => updateConfig({
                primaryColor: e.target.value
              })} data-id="element-1202" data-mp="d0i8eommxmg6" />
                <Select id="l-font" label="Tipografía" options={FONT_OPTIONS} value={draft.pageConfig.fontFamily} onChange={e => updateConfig({
                fontFamily: e.target.value
              })} data-id="element-1203" data-mp="322s7gp5bxlg" />
                <div data-id="element-1204" data-mp="vpgiivyc9uwl">
                  <p className="mb-2 text-sm font-medium text-foreground" data-id="element-1205" data-mp="vs2nqoxrbybn">
                    Logotipo de la landing
                  </p>
                  <LogoUploader value={draft.pageConfig.logoUrl} onChange={value => updateConfig({
                  logoUrl: value
                })} data-id="element-1206" data-mp="lzn65sqdhbra" />
                  <p className="mt-2 text-xs text-muted-foreground" data-id="element-1207" data-mp="gzhmozu9avka">
                    Si lo dejas vacío se usa el logotipo de tu empresa.
                  </p>
                </div>
              </div>

              {/* Biblioteca de componentes */}
              <div className="mt-5 rounded-2xl border border-border bg-background p-5" data-id="element-1208" data-mp="i1de697jatj2">
                <div className="mb-3 flex items-center gap-2" data-id="element-1209" data-mp="mtem5ixo5y3a">
                  <LayersIcon className="h-4 w-4 text-primary" aria-hidden="true" data-id="element-1210" data-mp="muzfx6c5td03" />
                  <h3 className="text-sm font-semibold text-foreground" data-id="element-1211" data-mp="8zne6ezl6m9o">
                    Biblioteca de componentes
                  </h3>
                </div>
                {components.length === 0 ? <p className="text-sm text-muted-foreground" data-id="element-1212" data-mp="11r6zq5jnoceu">
                    Guarda cualquier bloque con el icono de marcador para reutilizarlo
                    en otras landings.
                  </p> : <ul className="space-y-2" data-id="element-1213" data-mp="rrzpvsc4qzwh">
                    {components.map(component => <li key={component.id} className="flex items-center gap-2 rounded-xl border border-border p-3" data-id="element-1214" data-mp="wq9hxu2k5kdv">
                        <span className="min-w-0 flex-1" data-id="element-1215" data-mp="3cgvv8wmhpi3c">
                          <span className="block truncate text-sm font-medium text-foreground" data-id="element-1216" data-mp="3hi33ewsjojaj">
                            {component.name}
                          </span>
                          <span className="block text-xs text-muted-foreground" data-id="element-1217" data-mp="34yxzdux8e5il">
                            {component.category}
                          </span>
                        </span>
                        <Button size="sm" variant="secondary" onClick={() => addBlock(instantiateComponent(component))} data-id="element-1218" data-mp="3iwcrj7evcre9">
                          Insertar
                        </Button>
                        <button type="button" aria-label="Eliminar componente" onClick={() => deleteComponent(component.id)} className="rounded-md p-2 text-muted-foreground hover:text-red-600" data-id="element-1219" data-mp="2q7yuouzio14a">
                          <Trash2Icon className="h-4 w-4" data-id="element-1220" data-mp="301xqlc8zyons" />
                        </button>
                      </li>)}
                  </ul>}
              </div>
            </TabsContent>

            <TabsContent value="gracias" data-id="element-1221" data-mp="27hz4u5vuqhzt">
              <div className="space-y-4 rounded-2xl border border-border bg-background p-5" data-id="element-1222" data-mp="22kqn26em8fz3">
                <Input id="t-title" label="Título" value={draft.pageConfig.thankYou.title} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  title: e.target.value
                }
              })} data-id="element-1223" data-mp="z0aps2rl50rm" />
                <Input id="t-subtitle" label="Subtítulo" value={draft.pageConfig.thankYou.subtitle ?? ''} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  subtitle: e.target.value
                }
              })} data-id="element-1224" data-mp="18s6y06ndy79k" />
                <Textarea id="t-text" label="Texto" rows={3} value={draft.pageConfig.thankYou.text} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  text: e.target.value
                }
              })} data-id="element-1225" data-mp="1doxg0zir3cxm" />
                <Switch id="t-video" label="Mostrar vídeo" checked={Boolean(draft.pageConfig.thankYou.showVideo)} onChange={checked => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  showVideo: checked
                }
              })} data-id="element-1226" data-mp="3tgv0mbvc70iy" />
                <Input id="t-videourl" label="URL del vídeo (YouTube, Vimeo, Wistia o mp4)" value={draft.pageConfig.thankYou.videoUrl ?? ''} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  videoUrl: e.target.value
                }
              })} data-id="element-1227" data-mp="28xr8dbneu8vy" />
                <Input id="t-videothumb" label="Miniatura del vídeo (URL)" value={draft.pageConfig.thankYou.videoThumbnailUrl ?? ''} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  videoThumbnailUrl: e.target.value
                }
              })} data-id="element-1228" data-mp="1lb2myfz6zyal" />
                <Input id="t-image" label="Imagen (URL)" value={draft.pageConfig.thankYou.imageUrl ?? ''} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  imageUrl: e.target.value
                }
              })} data-id="element-1229" data-mp="35o1lktt6eogu" />
                <Switch id="t-cal" label="Mostrar botón de agendar llamada" checked={draft.pageConfig.thankYou.showCalendly} onChange={checked => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  showCalendly: checked
                }
              })} data-id="element-1230" data-mp="347nqtthkj395" />
                <Input id="t-calurl" label="URL de Calendly" value={draft.pageConfig.thankYou.calendlyUrl ?? ''} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  calendlyUrl: e.target.value
                }
              })} data-id="element-1231" data-mp="3rx8ayfpgk6k0" />
                <Switch id="t-wa" label="Mostrar botón de WhatsApp" checked={draft.pageConfig.thankYou.showWhatsapp} onChange={checked => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  showWhatsapp: checked
                }
              })} data-id="element-1232" data-mp="3e6danrt0yxs1" />
                <Input id="t-wanum" label="Número de WhatsApp" value={draft.pageConfig.thankYou.whatsappNumber ?? ''} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  whatsappNumber: e.target.value
                }
              })} data-id="element-1233" data-mp="1sf57sth8nuiw" />
                <Switch id="t-home" label="Mostrar botón de volver al inicio" checked={draft.pageConfig.thankYou.showHome} onChange={checked => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  showHome: checked
                }
              })} data-id="element-1234" data-mp="26eo3unwrzfeu" />
                <Input id="t-redirect" label="Redirección automática (URL)" value={draft.pageConfig.thankYou.autoRedirectUrl ?? ''} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  autoRedirectUrl: e.target.value
                }
              })} data-id="element-1235" data-mp="2l3au3knxxrku" />
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Vista previa editable, fija mientras el panel izquierdo se desplaza */}
        <div className="h-[860px] min-w-0 xl:sticky xl:top-4" data-id="element-1236" data-mp="2gfyvuxgneki6">
          <DevicePreview device={device} onDeviceChange={setDevice} toolbar={tab === 'gracias' ? <span className="text-xs text-muted-foreground" data-id="element-1238" data-mp="2q6jrcc10z8wv">
                  Vista previa de la página de gracias
                </span> : selectedBlock ? <span className="text-xs text-muted-foreground" data-id="element-1239" data-mp="22i0m65d9vc90">
                  Editando: {blockDefinition(selectedBlock.type).label}
                </span> : <span className="text-xs text-muted-foreground" data-id="element-1240" data-mp="1xk8th94szm3r">
                  Haz clic en una sección para editarla
                </span>} data-id="element-1237" data-mp="2v5d607s1t5ym">
            {tab === 'gracias' ? <ThankYouView config={draft.pageConfig.thankYou} logoUrl={draft.pageConfig.logoUrl ?? organization?.brand.logoUrl ?? organization?.logoUrl} primaryColor={draft.pageConfig.primaryColor} preview data-id="element-1241" data-mp="27eh6spl9j8hc" /> : <LandingRenderer landing={draft} form={form} preview selectedBlockId={selectedBlockId} onSelectBlock={setSelectedBlockId} viewport={device === 'mobile' || device === 'iphone16' ? 'mobile' : device === 'tablet' ? 'tablet' : 'desktop'} data-id="element-1242" data-mp="3iufotiyf54up" />}
          </DevicePreview>
        </div>
      </div>

      <Modal open={addOpen} onClose={() => setAddOpen(false)} title="Añadir sección" data-id="element-1243" data-mp="3hfd593il5to5">
        {components.length > 0 && <div className="mb-6" data-id="element-1244" data-mp="3lfx5ifyghdei">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground" data-id="element-1245" data-mp="cztuc084x6lf">
              Mis componentes
            </p>
            <ul className="grid gap-2 sm:grid-cols-2" data-id="element-1246" data-mp="hxlpd342t8tu">
              {components.map(component => <li key={component.id} data-id="element-1247" data-mp="46gvyiraxcry">
                  <button type="button" onClick={() => addBlock(instantiateComponent(component))} className="w-full rounded-xl border border-border p-3 text-left transition-colors hover:border-primary hover:bg-surface" data-id="element-1248" data-mp="45fhs5ezoa60">
                    <span className="block text-sm font-medium text-foreground" data-id="element-1249" data-mp="10peh6u7esnzz">
                      {component.name}
                    </span>
                    <span className="block text-xs text-muted-foreground" data-id="element-1250" data-mp="1ak3lfgwjcwg4">
                      {component.category}
                    </span>
                  </button>
                </li>)}
            </ul>
          </div>}
        <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground" data-id="element-1251" data-mp="11ivob25ra1nm">
          Secciones disponibles
        </p>
        <ul className="grid gap-2 sm:grid-cols-2" data-id="element-1252" data-mp="rmajohxneskb">
          {BLOCK_LIBRARY.map(definition => <li key={definition.type} data-id="element-1253" data-mp="mvu52ui3d0wd">
              <button type="button" onClick={() => addBlock(newBlock(definition.type))} className="w-full rounded-xl border border-border p-3 text-left transition-colors hover:border-primary hover:bg-surface" data-id="element-1254" data-mp="murb2dph2ic8">
                <span className="block text-sm font-medium text-foreground" data-id="element-1255" data-mp="dwfgy1ped2f1">
                  {definition.label}
                </span>
                <span className="block text-xs text-muted-foreground" data-id="element-1256" data-mp="8xvpjls1ayfa">
                  {definition.description}
                </span>
              </button>
            </li>)}
        </ul>
      </Modal>

      <Modal open={Boolean(saveComponentFor)} onClose={() => setSaveComponentFor(null)} title="Guardar en la biblioteca" footer={<>
            <Button variant="ghost" onClick={() => setSaveComponentFor(null)} data-id="element-1258" data-mp="3qxo2ybccoqv">
              Cancelar
            </Button>
            <Button onClick={confirmSaveComponent} data-id="element-1259" data-mp="8ps3rre0hx6r">Guardar componente</Button>
          </>} data-id="element-1257" data-mp="3q0rxhq9y0t0d">
        <Input id="component-name" label="Nombre del componente" value={componentName} onChange={e => setComponentName(e.target.value)} data-id="element-1260" data-mp="3798s5csg52v6" />
        <p className="mt-3 text-sm text-muted-foreground" data-id="element-1261" data-mp="3c4o41z6w8ooy">
          Se guardará con su contenido y su estilo actual para que puedas reutilizarlo
          en cualquier otra landing de tu empresa.
        </p>
      </Modal>
    </>;
}